// Circle membership. Core contract: membership = angefragt | bestätigt | abgelehnt | null plus joined boolean.
// Membership is only confirmed by joined === true from a fresh GET /member/groups.
import { own, hasOwn, lookup, validIsoDate } from "./memberDerive";

export const MESSAGE_MAX = 300;

const MEMBERSHIP = { angefragt: "requested", "bestätigt": "confirmed", bestaetigt: "confirmed", abgelehnt: "declined" };

export function membershipState(group) {
  if (!group || typeof group !== "object") return "unknown";
  const joined = own(group, "joined") === true;
  const raw = own(group, "membership");
  if (raw !== undefined && raw !== null) {
    const m = lookup(MEMBERSHIP, raw);
    if (m === "confirmed") return joined ? "member" : "unknown";
    if (m === "requested") return joined ? "unknown" : "join_pending";
    if (m === "declined") return joined ? "unknown" : "join_declined";
    return "unknown";
  }
  return joined ? "member" : "not_member";
}

// Withdrawal and leaving only count when the Core shows joined === false and membership === null (absent in older answers).
const cleared = (g) => own(g, "joined") === false && (own(g, "membership") === null || !hasOwn(g, "membership"));

// Circle status is separate from membership: only an own "pausiert" counts; missing, other or unreadable values claim nothing.
const GROUP_STATUS = { pausiert: "paused" };
export const isGroupPaused = (group) => lookup(GROUP_STATUS, own(group, "status")) === "paused";

export const PAUSED_WRITE_TEXT = "Dieser Kreis ist vorübergehend pausiert. Neue Beiträge, Kommentare und Nachrichten sind dort gerade nicht möglich.";

// A known pause blocks only joining; withdrawing a request and leaving stay possible.
export function groupActions(state, { blocked = false, paused = false } = {}) {
  if (blocked) return { join: false, withdraw: false, leave: false };
  return { join: !paused && state === "not_member", withdraw: state === "join_pending", leave: state === "member" };
}

// Optional message: trimmed, at most 300 Unicode characters (code points, not UTF-16 units).
export function joinMessage(raw) {
  const value = String(raw ?? "").trim();
  const length = Array.from(value).length;
  return { value, length, error: length > MESSAGE_MAX ? `Bitte höchstens ${MESSAGE_MAX} Zeichen (aktuell ${length}).` : "" };
}

// M70: only a released version counts – complete non-empty string list, positive integer version, valid date (validIsoDate from memberDerive).
export function groupRules(group) {
  const rules = own(group, "rules");
  const version = own(group, "rules_version");
  const updatedAt = own(group, "rules_updated_at");
  if (!Array.isArray(rules) || !rules.length) return null;
  for (let i = 0; i < rules.length; i += 1) if (!hasOwn(rules, i) || typeof rules[i] !== "string" || !rules[i].trim()) return null;
  if (!Number.isSafeInteger(version) || version < 1 || !validIsoDate(updatedAt)) return null;
  return { items: rules.map((r) => r.trim()), version, updatedAt };
}

const result = (kind, kindOf, state, message) => ({ kind, result: kindOf, state, message });

export function membershipOutcome(kind, { postError = null, ok = false, group = null } = {}) {
  if (postError) return result(kind, "failed", null, postError);
  const unknown = result(kind, "unknown", "unknown", kind === "join"
    ? "Übermittelt – dein Status im Kreis konnte aber nicht bestätigt werden. Prüfe ihn erneut."
    : "Übermittelt – dein Status im Kreis konnte nicht bestätigt werden. Prüfe ihn erneut.");
  if (!ok || !group) return unknown;
  const s = membershipState(group);
  if (s === "unknown") return unknown;
  if (kind === "join") {
    if (s === "member") return result(kind, "success", s, "Du bist jetzt Mitglied.");
    if (s === "join_pending") return result(kind, "pending", s, "Anfrage übermittelt. Du bist noch nicht als Mitglied bestätigt – bis dahin bleiben Mitglieder und Treffen geschützt.");
    if (s === "join_declined") return result(kind, "info", s, "Deine Beitrittsanfrage wurde abgelehnt.");
    return result(kind, "info", s, "Das VISIBILIS-System zeigt noch keine Anfrage oder Mitgliedschaft – es ist nichts bestätigt.");
  }
  if (cleared(group)) return result(kind, "success", s, kind === "withdraw" ? "Deine Anfrage ist zurückgezogen." : "Du hast den Kreis verlassen.");
  return result(kind, "info", s, kind === "withdraw"
    ? "Rückzug übermittelt – das VISIBILIS-System zeigt die Anfrage noch als offen."
    : "Austritt übermittelt – das VISIBILIS-System führt dich noch als Mitglied.");
}
